import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Download, FileText, Loader2, Search } from 'lucide-react';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Card } from '@shared/ui/containers/Card';
import { platform } from '@shared/lib/platform';
import { toast } from '@shared/ui/feedback/Toast';
import { useDebounce } from '@shared/hooks/useDebounce';
import { formatDate } from '@shared/lib/format';
import { pageCopy } from '@shared/lib/pageCopy';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import {
  downloadDocumentBlob,
  fetchDocuments,
  fetchDocumentCategories,
  type DocumentRecord,
} from './api';

function metaLine(doc: DocumentRecord): string {
  const parts: string[] = [];
  if (doc.category) parts.push(doc.category);
  const dateLabel = formatDate(doc.uploaded_at);
  if (dateLabel) parts.push(dateLabel);
  if (doc.file_size_bytes) {
    const mb = doc.file_size_bytes / 1_048_576;
    parts.push(mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(doc.file_size_bytes / 1024))} KB`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'Document';
}

/**
 * Documents — estate header, folder pills, searchable list. Download uses the
 * authenticated tenant API stream (not a raw storage URL).
 */
export function Documents(): JSX.Element {
  const copy = pageCopy().documents;
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search, 250);

  const categoriesQuery = useQuery({
    queryKey: ['document-categories'],
    queryFn: fetchDocumentCategories,
  });

  const categories = useMemo(
    () => [{ category_id: 'all', name: 'All' }, ...(categoriesQuery.data ?? [])],
    [categoriesQuery.data],
  );

  const folderNameById = useMemo(
    () => new Map(categories.filter((c) => c.category_id !== 'all').map((c) => [c.category_id, c.name])),
    [categories],
  );

  const documentsQuery = useQuery({
    queryKey: ['documents', activeCategory],
    queryFn: () =>
      fetchDocuments(
        activeCategory === 'all' ? undefined : activeCategory,
        folderNameById,
      ),
  });

  const filtered = useMemo(() => {
    const docs = documentsQuery.data ?? [];
    const needle = debouncedSearch.trim().toLowerCase();
    if (!needle) return docs;
    return docs.filter(
      (d) =>
        d.title.toLowerCase().includes(needle) ||
        d.description?.toLowerCase().includes(needle) ||
        d.category?.toLowerCase().includes(needle) ||
        d.file_name.toLowerCase().includes(needle),
    );
  }, [documentsQuery.data, debouncedSearch]);

  const handleDownload = async (doc: DocumentRecord): Promise<void> => {
    if (downloadingId) return;
    setDownloadingId(doc.document_id);
    try {
      const blob = await downloadDocumentBlob(doc.document_id);
      const filename = sanitizeFilename(doc.file_name || doc.title);
      await saveBlobAsDownload(blob, filename, doc.title);
      toast.success('Download started', doc.title);
    } catch (e) {
      toast.danger("Couldn't download", e instanceof Error ? e.message : 'Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePreview = (doc: DocumentRecord): void => {
    const params = new URLSearchParams({
      title: doc.title,
      file: doc.file_name,
    });
    navigate(`/app/documents/${doc.document_id}/view?${params.toString()}`);
  };

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader eyebrow={copy.eyebrow} title={copy.title} subtitle={copy.subtitle} />

      {categories.length > 1 ? (
        <div className="flex gap-2 overflow-x-auto px-5 pt-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {categories.map((cat) => {
            const on = activeCategory === cat.category_id;
            return (
              <button
                key={cat.category_id}
                type="button"
                onClick={() => setActiveCategory(cat.category_id)}
                className={
                  'shrink-0 rounded-pill px-4 py-2 font-sans text-body-sm font-medium transition-colors ' +
                  (on ? 'bg-primary-700 text-white' : 'bg-card hairline text-ink-secondary')
                }
              >
                {cat.name}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="px-5 pt-3">
        <div className="flex items-center gap-2 rounded-pill bg-card hairline px-4 py-2.5">
          <Search className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents…"
            className="w-full bg-transparent font-sans text-body text-ink placeholder:text-ink-muted focus:outline-none"
          />
        </div>
      </div>

      <div className="flex flex-col gap-3 px-5 pt-4 pb-safe-or-6">
        {documentsQuery.isLoading ? (
          <>
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </>
        ) : documentsQuery.isError ? (
          <ErrorState onRetry={() => documentsQuery.refetch()} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<FileText className="h-7 w-7" strokeWidth={1.5} />}
            title={search ? 'No matches.' : 'Nothing here yet.'}
            description={
              search
                ? 'Try a different search term.'
                : "When the estate publishes documents you'll see them here."
            }
          />
        ) : (
          filtered.map((doc) => {
            const busy = downloadingId === doc.document_id;
            return (
              <Card key={doc.document_id} noGrain className="rounded-xl p-4 text-left disabled:opacity-60">
                <div className="flex w-full items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                    <FileText className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <button
                    type="button"
                    disabled={!!downloadingId}
                    onClick={() => handlePreview(doc)}
                    className="min-w-0 flex-1 text-left transition-transform duration-fast active:scale-[0.99]"
                  >
                    <p className="truncate font-sans text-body font-semibold leading-snug text-ink">
                      {doc.title}
                    </p>
                    <p className="mt-1 truncate font-sans text-body-sm text-ink-secondary">
                      {metaLine(doc)}
                    </p>
                  </button>
                  <button
                    type="button"
                    aria-label={`Download ${doc.title}`}
                    disabled={!!downloadingId}
                    onClick={() => void handleDownload(doc)}
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-pill text-ink-muted transition-colors hover:bg-sunken hover:text-ink disabled:opacity-60"
                  >
                    {busy ? (
                      <Loader2 className="h-4.5 w-4.5 animate-spin" strokeWidth={1.75} />
                    ) : (
                      <Download className="h-4.5 w-4.5" strokeWidth={1.75} />
                    )}
                  </button>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}

async function saveBlobAsDownload(blob: Blob, filename: string, title: string): Promise<void> {
  if (platform.isNative()) {
    const base64 = await blobToBase64(blob);
    const path = `documents/${filename}`;
    await Filesystem.writeFile({
      path,
      data: base64,
      directory: Directory.Cache,
      recursive: true,
    });
    const { uri } = await Filesystem.getUri({ directory: Directory.Cache, path });
    await Share.share({ title, url: uri, dialogTitle: 'Save document' });
    return;
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(',')[1] ?? '');
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(blob);
  });
}

function sanitizeFilename(name: string): string {
  const trimmed = name.replace(/[^a-z0-9._-]+/gi, '_').slice(0, 120);
  return trimmed || 'document';
}
